import * as React from 'react';

/**
 * Catching the browser's install offer instead of letting it fire (`MEM-040` AC-4).
 *
 * ## What `beforeinstallprompt` does if you ignore it
 *
 * Chromium fires it when it decides a site is installable, and its default action is to show the
 * browser's own mini-infobar — at whatever moment the heuristics happen to fire, which is
 * routinely the member's first second on the page. `preventDefault()` suppresses that, and the
 * event object is kept because it is the **only** handle to the real install dialogue: it can be
 * prompted exactly once, it cannot be constructed, and once it is dropped there is no way to ask
 * again until the browser decides to fire another one.
 *
 * So this captures and holds. It does not prompt.
 *
 * ## Why nothing here surfaces it
 *
 * `MEM-040` AC-4 is "captured rather than surfaced automatically", and **when** to surface it is
 * a different story with a different answer: `PLT-008` AC-5 rules that the offer appears once
 * after a member's first completed registration, at most twice in total, and never once
 * installed. That discipline needs a record of how many times a member has been asked, which is
 * account state this story does not own. Building a banner here would be building the thing
 * `PLT-008` then has to take apart, and the version that ships in the meantime is the ambush
 * `MEM-040` exists to prevent.
 *
 * What this exposes is a hook with `canInstall` and a `promptInstall()` that only ever runs from
 * a member's own tap. Today's only caller is the deliberate control on My Account — a member who
 * went looking for it. `PLT-008` adds the timing without touching the capture.
 *
 * ## `appinstalled`, and why the handle is dropped
 *
 * A prompt that has been accepted is spent, and a captured event that has been prompted is
 * inert — offering the control again would be offering a button that does nothing. So the
 * installed event clears it.
 */

/**
 * The slice of `BeforeInstallPromptEvent` this uses. Typed here because it is not in the DOM
 * library — it is a Chromium extension, and the shape is small enough that a local interface is
 * more honest than a cast to `any`.
 */
export interface InstallEligibilityEvent extends Event {
  prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPromptState {
  /** True only once the browser has said the app is installable *and* the event is held. */
  readonly canInstall: boolean;
  /**
   * Runs the browser's install dialogue. **Call this from a member's own interaction only** —
   * Chromium refuses a `prompt()` outside a user gesture, and the refusal is silent.
   */
  readonly promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/** Whether the app is already running as an installed app; the control is pointless if it is. */
export function isRunningInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standaloneDisplay = window.matchMedia?.('(display-mode: standalone)').matches === true;
  // iOS reports installation on `navigator` rather than through a display-mode query.
  const iosStandalone = (navigator as { readonly standalone?: unknown }).standalone === true;
  return standaloneDisplay || iosStandalone;
}

export function useInstallPrompt(): InstallPromptState {
  const captured = React.useRef<InstallEligibilityEvent | null>(null);
  const [canInstall, setCanInstall] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const capture = (event: Event): void => {
      // The whole point of the listener. Without this the browser draws its own bar, at its own
      // moment, over whatever the member was reading.
      event.preventDefault();
      captured.current = event as InstallEligibilityEvent;
      setCanInstall(true);
    };
    const forget = (): void => {
      captured.current = null;
      setCanInstall(false);
    };

    window.addEventListener('beforeinstallprompt', capture);
    window.addEventListener('appinstalled', forget);
    return () => {
      window.removeEventListener('beforeinstallprompt', capture);
      window.removeEventListener('appinstalled', forget);
    };
  }, []);

  const promptInstall = React.useCallback(async (): Promise<
    'accepted' | 'dismissed' | 'unavailable'
  > => {
    const event = captured.current;
    if (event === null) return 'unavailable';
    // Spent either way: the event cannot be prompted twice, and holding a used one would leave
    // `canInstall` true over a control that no longer does anything.
    captured.current = null;
    setCanInstall(false);
    try {
      await event.prompt();
      const choice = await event.userChoice;
      return choice.outcome;
    } catch {
      return 'unavailable';
    }
  }, []);

  return { canInstall, promptInstall };
}
